import axios from 'axios';
import { MemreButton, MemreFlexBox } from 'components/core';
import { SupportLink } from 'components/SupportLink';
import { red as errorRed, hexToRGB, lightGray, mainBlue, textGray, veryLightGray, white } from 'core/colors';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useUserCsvUploads } from 'hooks/useUserCsvUploads';
import React, { useRef, useState } from 'react';
import { queryCache } from 'react-query';
import styled from 'styled-components';

export const CsvUploadModal = ({ onClose }: { onClose: () => void }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadId, setUploadId] = useState<string | null>(null);
  const { refetch } = useUserCsvUploads();
  const [isDragging, setIsDragging] = useState(false);

  /**
   * Steps:
   * 'upload' -> 'matchColumns' -> 'review' (dry-run) -> 'complete' (final parse)
   */
  const [step, setStep] = useState<'upload' | 'matchColumns' | 'review' | 'complete'>('upload');

  const [nameColumn, setNameColumn] = useState<string>('');
  const [identifierColumn, setIdentifierColumn] = useState<string>('');
  const [identifierType, setIdentifierType] = useState<'email' | 'member_id'>('email');
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvPreview, setCsvPreview] = useState<Array<Record<string, string>>>([]);
  const [importSummary, setImportSummary] = useState<any>(null);

  const [status, setStatus] = useState<'idle' | 'loading' | 'error' | 'success'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [isRemoval, setIsRemoval] = useState<boolean>(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const user = useCurrentUser();
  const memberIdSlug = user?.primaryPartner.partnerSettings.memberIdSlug;

  // ---------------------------
  // Step 1: Upload CSV
  // ---------------------------
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    // Only set isDragging to false if we're leaving the dropzone (not entering a child element)
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      // Check if the file is a CSV
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        setSelectedFile(file);
      } else {
        setErrorMessage('Please upload a CSV file.');
      }
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return alert('Select a file.');
    try {
      setStatus('loading');
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(`/api/v3/partners/${user.primaryPartner.id}/user_csv_uploads`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const csvExampleContent = response.data.data.meta['csv-example-content'];
      if (!csvExampleContent || !Array.isArray(csvExampleContent)) {
        throw new Error('Invalid CSV');
      }

      // Check if "remove_user" column is present
      const headers = csvExampleContent.map((col: any[]) => col[0]);
      setIsRemoval(headers.includes('remove_user'));

      // Build preview data
      const numRows = csvExampleContent[0][1].split(',').length;
      const previewData: Array<Record<string, string>> = [];
      for (let i = 0; i < numRows; i++) {
        const row: Record<string, string> = {};
        for (const column of csvExampleContent) {
          const [colName, colData] = column;
          row[colName] = colData.split(',')[i] || '';
        }
        previewData.push(row);
      }

      setCsvHeaders(headers);
      setCsvPreview(previewData);
      setUploadId(response.data.data.id);
      setStatus('success');
      setStep('matchColumns');
    } catch (err) {
      console.error(err);
      setErrorMessage('Upload failed.');
      setStatus('error');
    }
  };

  // ---------------------------
  // Step 2: Match Columns (initiate a DRY-RUN parse)
  // ---------------------------
  const handleImport = async () => {
    if (!nameColumn || !identifierColumn) {
      alert('Select columns.');
      return;
    }
    if (!uploadId) return;

    try {
      setStatus('loading');
      await axios.post(`/api/v3/user_csv_uploads/${uploadId}/parse_background`, {
        dry_run: true,
        name_column: nameColumn,
        email_column: identifierType === 'email' ? identifierColumn : undefined,
        id_column: identifierType === 'member_id' ? identifierColumn : undefined,
      });

      // Start a short poll for the DRY-RUN results
      pollDryRunResults(uploadId, () => {
        setStep('review');
      });
    } catch (err) {
      console.error(err);
      setErrorMessage('Import failed.');
      setStatus('error');
    }
  };

  // DRY-RUN poll only
  const pollDryRunResults = (uploadId: string, onDone: () => void) => {
    let attempts = 0;
    const maxAttempts = 500;
    const intervalMs = 2000;

    const timer = setInterval(async () => {
      attempts++;
      try {
        setStatus('loading');
        const res = await axios.get(`/api/v3/user_csv_uploads/${uploadId}`);
        const record = res.data.data;
        // If the dry-run summary is present, we can stop polling
        if (record?.meta?.['parsed-count']) {
          setImportSummary(record);
          setStatus('success');
          clearInterval(timer);
          onDone();
        } else if (attempts >= maxAttempts) {
          setErrorMessage('Timed out waiting for dry-run results. Please try again.');
          setStatus('error');
          clearInterval(timer);
        }
      } catch (err) {
        console.error(err);
        setErrorMessage('Summary fetch failed.');
        setStatus('error');
        clearInterval(timer);
      }
    }, intervalMs);
  };

  // ---------------------------
  // Step 3: Review => final parse, no poll
  // ---------------------------
  const handleConfirmImport = async () => {
    if (!uploadId) return;

    try {
      setStatus('loading');
      await axios.post(`/api/v3/user_csv_uploads/${uploadId}/parse_background`, {
        dry_run: false,
        name_column: nameColumn,
        email_column: identifierType === 'email' ? identifierColumn : undefined,
        id_column: identifierType === 'member_id' ? identifierColumn : undefined,
      });
      // We WON'T poll for final results now. We'll let the
      // user see them in the new CSV Uploads Status panel on the People page.
      setStatus('success');
      setStep('complete');
      // Invalidate the old data so the table refreshes if needed
      queryCache.invalidateQueries('partnerUsers');
    } catch (err) {
      console.error(err);
      setErrorMessage('Import error.');
      setStatus('error');
    }
  };

  // ---------------------------
  // Step 4: Complete => close
  // ---------------------------
  const closeModal = () => {
    onClose();
    setStatus('idle');
    setErrorMessage('');
    setSelectedFile(null);
    setImportSummary(null);
    setStep('upload');
    refetch();
  };

  // A helper to more cleanly render the parse stats if present
  const renderStats = () => {
    const stats = importSummary?.meta?.['parsed-count'];
    if (!stats) return <p>No summary available.</p>;

    const { added = 0, modified = 0, omitted = 0, removed = 0 } = stats;
    return (
      <StatsContainer>
        <p>Preview of changes from this CSV:</p>
        <ul>
          <li>
            <strong>Added:</strong> {added}
          </li>
          <li>
            <strong>Modified:</strong> {modified}
          </li>
          <li>
            <strong>Omitted:</strong> {omitted}
          </li>
          <li>
            <strong>Removed:</strong> {removed}
          </li>
        </ul>
      </StatsContainer>
    );
  };

  return (
    <Overlay>
      <Content>
        {/* ----- Step 1: Upload ----- */}
        {step === 'upload' && (
          <>
            <h2>Upload CSV</h2>
            <Instructions>
              <p>
                <strong>CSV Guidelines:</strong>
              </p>
              <ul>
                <li>Save as Unicode (UTF-8).</li>
                <li>
                  To add users, include headers <code>Full Name</code> & <code>Email</code>.
                </li>
                <li>
                  To remove users, add header <code>remove_user</code> with value <code>remove_user</code>.
                </li>
              </ul>
              <p>
                <SupportLink
                  type="link"
                  url="https://www.cerego.com/support/importing-people-from-csv-files"
                  title="Learn more"
                />
              </p>
            </Instructions>

            <DropZone
              isDragging={isDragging}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <HiddenInput type="file" accept=".csv" ref={fileInputRef} onChange={handleFileChange} />

              {selectedFile ? (
                <SelectedFileInfo>
                  <FileIcon>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z"
                        fill={veryLightGray}
                        stroke={mainBlue}
                        strokeWidth="2"
                      />
                      <path d="M14 2V8H20" stroke={mainBlue} strokeWidth="2" />
                      <path d="M8 14H16" stroke={mainBlue} strokeWidth="2" />
                      <path d="M8 18H12" stroke={mainBlue} strokeWidth="2" />
                    </svg>
                  </FileIcon>
                  <FileName>{selectedFile.name}</FileName>
                  <MemreButton
                    text="Change"
                    variant="outlined"
                    color="secondary"
                    onClick={handleFileButtonClick}
                    size="small"
                  />
                </SelectedFileInfo>
              ) : (
                <DropContent>
                  <FileIcon>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z"
                        fill={veryLightGray}
                        stroke={mainBlue}
                        strokeWidth="2"
                      />
                      <path d="M14 2V8H20" stroke={mainBlue} strokeWidth="2" />
                      <path d="M12 18V12" stroke={mainBlue} strokeWidth="2" />
                      <path d="M9 15L12 12L15 15" stroke={mainBlue} strokeWidth="2" />
                    </svg>
                  </FileIcon>
                  <p>Drag and drop your CSV file here</p>
                  <p>or</p>
                  <MemreButton
                    text="Choose File"
                    variant="contained"
                    color="secondary"
                    onClick={handleFileButtonClick}
                    size="medium"
                  />
                </DropContent>
              )}
            </DropZone>

            {errorMessage && <ErrorText>{errorMessage}</ErrorText>}

            <MemreFlexBox direction="row" gap={2} justify="flex-end" sx={{ marginTop: '20px' }}>
              <MemreButton text="Cancel" variant="outlined" color="secondary" onClick={closeModal} size="medium" />
              <MemreButton
                text={status === 'loading' ? 'Uploading...' : 'Next'}
                variant="contained"
                color="secondary"
                onClick={handleFileUpload}
                size="medium"
                disabled={!selectedFile || status === 'loading'}
              />
            </MemreFlexBox>
          </>
        )}

        {/* ----- Step 2: Match Columns ----- */}
        {step === 'matchColumns' && (
          <>
            <h2>Match Columns</h2>
            <ColumnSelector>
              <label>
                Name Column:
                <select value={nameColumn} onChange={(e) => setNameColumn(e.target.value)}>
                  <option value="">Select</option>
                  {csvHeaders.map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Identifier Type:
                <select
                  value={identifierType}
                  onChange={(e) => setIdentifierType(e.target.value as 'email' | 'member_id')}
                >
                  <option value="email">Email</option>
                  <option value="member_id">{memberIdSlug || 'Member ID'}</option>
                </select>
              </label>
              <label>
                {identifierType === 'email' ? 'Email Column:' : `${memberIdSlug || 'Member ID'} Column:`}
                <select value={identifierColumn} onChange={(e) => setIdentifierColumn(e.target.value)}>
                  <option value="">Select</option>
                  {csvHeaders.map((h) => (
                    <option key={h} value={h}>
                      {h}
                    </option>
                  ))}
                </select>
              </label>
            </ColumnSelector>

            <TableWrapper>
              <table>
                <thead>
                  <tr>
                    {csvHeaders.map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {csvPreview.map((row, i) => (
                    <tr key={i}>
                      {csvHeaders.map((h) => (
                        <td key={h}>{row[h]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrapper>

            {errorMessage && <ErrorText>{errorMessage}</ErrorText>}

            <MemreFlexBox direction="row" gap={2} justify="flex-end" sx={{ marginTop: '20px' }}>
              <MemreButton text="Cancel" variant="outlined" color="secondary" onClick={closeModal} size="medium" />
              <MemreButton
                text={status === 'loading' ? 'Processing...' : 'Next'}
                variant="contained"
                color="secondary"
                onClick={handleImport}
                size="medium"
                disabled={status === 'loading'}
              />
            </MemreFlexBox>
          </>
        )}

        {/* ----- Step 3: Review (dry-run) ----- */}
        {step === 'review' && (
          <>
            <h2>Review</h2>
            {status === 'loading' ? (
              <p>Processing...</p>
            ) : (
              <>
                {renderStats()}
                <MemreFlexBox direction="row" gap={2} justify="flex-end" sx={{ marginTop: '20px' }}>
                  <MemreButton text="Cancel" variant="outlined" color="secondary" onClick={closeModal} size="medium" />
                  <MemreButton
                    text={isRemoval ? 'Confirm Removal' : 'Confirm Import'}
                    variant="contained"
                    color="secondary"
                    onClick={handleConfirmImport}
                    size="medium"
                  />
                </MemreFlexBox>
              </>
            )}
            {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
          </>
        )}

        {/* ----- Step 4: Complete (just confirm job started) ----- */}
        {step === 'complete' && (
          <>
            <h2>Import Started</h2>
            <p>
              We've started processing your CSV in the background. You can close this window now. You'll find final
              results in the <strong>Recent CSV Uploads</strong> section on the People page once it's done.
            </p>
            <MemreFlexBox direction="row" gap={2} justify="flex-end" sx={{ marginTop: '20px' }}>
              <MemreButton text="Close" variant="contained" color="secondary" onClick={closeModal} size="medium" />
            </MemreFlexBox>
          </>
        )}
      </Content>
    </Overlay>
  );
};

/* --------------------- STYLES --------------------- */

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const Content = styled.div`
  background: ${white};
  padding: 20px;
  width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  border-radius: 4px;
`;

const Instructions = styled.div`
  margin: 16px 0;
  font-size: 14px;

  ul {
    margin: 8px 0 0 20px;
  }

  code {
    background: ${veryLightGray};
    padding: 2px 4px;
    border-radius: 3px;
  }

  a {
    color: ${mainBlue};
    text-decoration: none;
  }
`;

// New styled components for drag and drop functionality
const DropZone = styled.div<{ isDragging: boolean }>`
  margin: 20px 0;
  border: 2px dashed ${(props) => (props.isDragging ? mainBlue : lightGray)};
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  background-color: ${(props) => (props.isDragging ? hexToRGB(mainBlue, 0.1) : veryLightGray)};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${mainBlue};
    background-color: ${hexToRGB(mainBlue, 0.05)};
  }
`;

const DropContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;

  p {
    margin: 0;
    color: ${textGray};
  }
`;

const FileIcon = styled.div`
  margin-bottom: 8px;
`;

const SelectedFileInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const FileName = styled.span`
  font-weight: 500;
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HiddenInput = styled.input`
  display: none;
`;

const ColumnSelector = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;

  label {
    font-size: 14px;
  }

  select {
    margin-left: 8px;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;

  table {
    width: 100%;
    border-collapse: collapse;
  }

  th,
  td {
    border: 1px solid ${lightGray};
    padding: 8px;
    text-align: left;
  }

  th {
    background: ${veryLightGray};
  }
`;

const ErrorText = styled.p`
  color: ${errorRed};
  font-size: 14px;
  margin-top: 10px;
`;

const StatsContainer = styled.div`
  margin-top: 1rem;

  ul {
    margin: 8px 0 0 1rem;
  }
`;
